import {
  APP_SETTINGS_SERVICE_TOKEN,
  BACKGROUND_MODE_COMPONENT_TOKEN,
  CheatComponent,
  CHEAT_COMPONENT_TOKEN,
  COLLAPSE_ANIMATION_COMPONENT_TOKEN,
  CollapseGameConfigComponent,
  CollapseGameStateMachine,
  CUSTOM_COMPONENTS_PROVIDER_TOKEN,
  CustomLoader,
  ElementsStateController,
  FreeFallIconsSoundModelComponent,
  FreeFallReelsConfigComponent,
  FreeFallReelsEngineComponent,
  FreeFallSpinViewMachineListener,
  FreeSpinsModeProvider,
  GAME_STATE_MACHINE_TOKEN,
  FAST_SPINS_COMPONENT,
  GameProvider,
  InitialReelsProvider,
  LongStoppingIconEnumeratorProvider,
  MultiSceneIconResourceProvider,
  ORIENTATION_HANDLER_COMPONENT_TOKEN,
  REQUIRED_COMPONENTS_PROVIDER_TOKEN,
  ResponseInitialReelsComponent,
  SlotSoundConfigEntry,
  SlotSoundType,
  T_AbstractIconResourceProvider,
  T_BaseSlotSoundController,
  T_ElementsStateController,
  T_EndFreeSpinsPopupComponent,
  T_EpicWinPopupProvider,
  T_IconEnumeratorComponent,
  T_IconsSoundModelComponent,
  T_IFreeSpinsModeProvider,
  T_IGameConfigProvider,
  T_IInitialReelsProvider,
  T_InitialReelsComponent,
  T_IReelsConfigProvider,
  T_ISlotGameEngineProvider,
  T_ISlotPrimaryAnimationProvider,
  T_IStartSlotActionProvider,
  T_IStopSlotActionProvider,
  T_OptionalLoader,
  T_SpinViewMachineListener,
  T_StartFreeSpinsPopupComponent,
  T_SubstituteIconComponent,
  TIME_SCALE_COMPONENT_TOKEN,
  TtfEditorPopup,
  TTF_EDITOR_POPUP_TOKEN,
  TestPerformanceMonitorComponent,
  TEST_PERFORMANCE_MONITOR_COMPONENT_TOKEN,
  WinStrategy,
  FastSpinsComponent,
  PAYTABLE_POPUP_COMPONENT_TOKEN,
  BUY_FEATURE_POPUP_COMPONENT_TOKEN,
} from '@cgs/machines';
import { EnvironmentConfig } from '@cgs/shared';
import { Container, InstanceLifetime, SoundState } from '@cgs/wge';
import { SantaOrientationHandlerComponent } from './resize-handler/santa-orientation-handler.component';
import { SantaAppSettingsService } from './slot/app-settings/santa-app-settings.service';
import { SantaBackgroundImageComponent } from './slot/background-image/santa-background-image.component';
import { SantaBaseSlotSoundController } from './slot/base-slot-sound/santa-base-slot-sound.controller';
import { SantaBuyFeaturePopup } from './slot/buy-feature/santa-buy-feature-popup';
import {
  SANTA_BACKGROUND_IMAGE_RESIZE_COMPONENT_TOKEN,
  SantaBackgroundImageResizeComponent,
} from './slot/components/background-image-resize/santa-background-image-resize';
import {
  SANTA_COLLAPSE_LOW_WIN_COMPONENT_TOKEN,
  SantaCollapseLowWinComponent,
} from './slot/components/collapse-low-win/santa-collapse-low-win.component';
import {
  COLLAPSE_WIN_COMPONENT_TOKEN,
  CollapseWinComponent,
} from './slot/components/collapse-win/collapse-win.component';
import {
  SANTA_BOUNCE_ON_STOP_COMPONENT_TOKEN,
  SantaBounceOnStopComponent,
} from './slot/components/collapse/santa-bounce-on-stop.component';
import { SantaCollapseAnimationComponent } from './slot/components/collapse/santa-collapse-animation.component';
import { SantaEpicWinPopupProvider } from './slot/components/epic-win/santa-epic-win-popup.provider';
import { SantaFreeFallSpinActionComponent } from './slot/components/fall/santa-free-fall-spin-action-component';
import { SantaFreeFallStopActionComponent } from './slot/components/fall/santa-free-fall-stop-action-component';
import { SantaEndFreeSpinsPopupComponent } from './slot/components/free-spins-popup/end-free-spins-popup/santa-end-free-spins-popup.component';
import { SantaStartFreeSpinsPopupComponent } from './slot/components/free-spins-popup/start-free-spins-popup/santa-free-spins-begin-popup.component';
import {
  SANTA_FREE_SPINS_TOTAL_WIN_COMPONENT_TOKEN,
  SantaFreeSpinsTotalWinComponent,
} from './slot/components/free-spins-total-win/santa-free-spins-total-win.component';
import {
  SANTA_MULTIPLIER_HOLDER_COMPONENT_TOKEN,
  SantaMultiplierHolderComponent,
} from './slot/components/multiplier-holder/santa-multiplier-holder.component';
import {
  SANTA_TOP_MULTIPLIER_COMPONENT_TOKEN,
  SantaTopMultiplierComponent,
} from './slot/components/multiplier/santa-top-multiplier.component';
import {
  SANTA_SKIP_ANIMATIONS_COMPONENT_TOKEN,
  SantaSkipAnimationsComponent,
} from './slot/components/skip-animations/santa-skip-animations.component';
import { SantaGameRules } from './slot/hud/santa-game-rules';
import { SantaSlotAnimationProvider } from './slot/slot-animation/santa-slot-animation.provider';
import { SantaTimeScaleComponent } from './slot/components/santa-time-scale.component';

export class SantaGameProvider extends GameProvider {
  constructor(
    container: Container,
    private isTrifecta: boolean = false
  ) {
    super(container);
  }

  public override register(): void {
    super.register();

    this.container.register(CUSTOM_COMPONENTS_PROVIDER_TOKEN, (container: Container) => {
      container.register(TIME_SCALE_COMPONENT_TOKEN, () => new SantaTimeScaleComponent(container), {
        lifetime: InstanceLifetime.Singleton,
      });

      container.register(
        T_AbstractIconResourceProvider,
        () => new MultiSceneIconResourceProvider(container, 'icons', 'icons'),
        { lifetime: InstanceLifetime.Singleton }
      );
      container.register(
        T_ISlotGameEngineProvider,
        (container) => new FreeFallReelsEngineComponent(container),
        { lifetime: InstanceLifetime.Singleton }
      );
      container.register(
        T_IReelsConfigProvider,
        (container) => new FreeFallReelsConfigComponent(container),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(
        T_ISlotPrimaryAnimationProvider,
        (container) => new SantaSlotAnimationProvider(container),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(
        T_IconEnumeratorComponent,
        (container) => new LongStoppingIconEnumeratorProvider(container, []),
        { lifetime: InstanceLifetime.Singleton }
      );
      container.register(
        T_SpinViewMachineListener,
        (container) => new FreeFallSpinViewMachineListener(container),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(
        GAME_STATE_MACHINE_TOKEN,
        (container) => new CollapseGameStateMachine(container).initialize(),
        { lifetime: InstanceLifetime.Singleton }
      );
      container.register(
        T_IStopSlotActionProvider,
        (container) => new SantaFreeFallStopActionComponent(container),
        { lifetime: InstanceLifetime.Singleton }
      );
      container.register(
        T_ElementsStateController,
        (container) =>
          new ElementsStateController(
            container,
            ElementsStateController.defaultStates('table', ['reel_bg'])
          ),
        { lifetime: InstanceLifetime.Singleton }
      );
      container.register(
        T_IFreeSpinsModeProvider,
        (container) =>
          new FreeSpinsModeProvider(container, 'reel_bg', '{0}', ['freeGame'], null, ''),
        { lifetime: InstanceLifetime.Singleton }
      );
      container.register(
        T_StartFreeSpinsPopupComponent,
        (container) => new SantaStartFreeSpinsPopupComponent(container),
        { lifetime: InstanceLifetime.Singleton }
      );
      container.register(
        T_EndFreeSpinsPopupComponent,
        (container) =>
          new SantaEndFreeSpinsPopupComponent(container, {
            stopBackgroundSound: true,
            useFreeSpinMode: true,
            closeOnAnimFinished: true,
            closeButtonsName: 'btnContinue',
          }),
        { lifetime: InstanceLifetime.Singleton }
      );

      this.container.register(CHEAT_COMPONENT_TOKEN, (c) => new CheatComponent(c), {
        lifetime: InstanceLifetime.Singleton,
      });

      container.register(
        T_IconsSoundModelComponent,
        (container) => new FreeFallIconsSoundModelComponent(container, { maxIconId: 20 }),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(
        COLLAPSE_ANIMATION_COMPONENT_TOKEN,
        (container) =>
          new SantaCollapseAnimationComponent(container, {
            collapseSpeed: 3000,
            winStrategy: WinStrategy.WinPositions,
          }),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(
        SANTA_BOUNCE_ON_STOP_COMPONENT_TOKEN,
        (container) => new SantaBounceOnStopComponent(container),
        { lifetime: InstanceLifetime.Singleton }
      );

      // 'loading' is preloaded here (not just 'additional') so the start-page
      // scenes (loading/scene, loading/sceneLogo) are cached before the loader
      // finishes — otherwise the slot flashes before the start page appears.
      container.register(
        T_OptionalLoader,
        (container) => new CustomLoader(container, ['additional', 'loading']),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(
        COLLAPSE_WIN_COMPONENT_TOKEN,
        (container) => new CollapseWinComponent(container),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(
        BACKGROUND_MODE_COMPONENT_TOKEN,
        (container) => new SantaBackgroundImageComponent(container),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(T_EpicWinPopupProvider, (c) => new SantaEpicWinPopupProvider(c), {
        lifetime: InstanceLifetime.Singleton,
      });

      container.register(
        SANTA_COLLAPSE_LOW_WIN_COMPONENT_TOKEN,
        (container) => new SantaCollapseLowWinComponent(container),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(
        SANTA_MULTIPLIER_HOLDER_COMPONENT_TOKEN,
        (container) => new SantaMultiplierHolderComponent(container),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(
        SANTA_TOP_MULTIPLIER_COMPONENT_TOKEN,
        (container) => new SantaTopMultiplierComponent(container),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      container.register(BUY_FEATURE_POPUP_COMPONENT_TOKEN, (c) => new SantaBuyFeaturePopup(c), {
        lifetime: InstanceLifetime.Singleton,
      });

      container.register(APP_SETTINGS_SERVICE_TOKEN, (c) => new SantaAppSettingsService(c), {
        lifetime: InstanceLifetime.Singleton,
      });

      container.register(
        FAST_SPINS_COMPONENT,
        (container) => new FastSpinsComponent(container, 1.5),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      container.register(
        T_BaseSlotSoundController,
        (c) => {
          return new SantaBaseSlotSoundController(c, [
            new SlotSoundConfigEntry(SlotSoundType.Background, SoundState.Playing, 'show'),
            new SlotSoundConfigEntry(SlotSoundType.Background, SoundState.Stopped, 'hide'),
            new SlotSoundConfigEntry(SlotSoundType.Background, SoundState.Paused, 'pause'),
          ]);
        },
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      container.register(
        ORIENTATION_HANDLER_COMPONENT_TOKEN,
        (c) => new SantaOrientationHandlerComponent(c),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(
        SANTA_SKIP_ANIMATIONS_COMPONENT_TOKEN,
        (container) => new SantaSkipAnimationsComponent(container),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(
        T_IInitialReelsProvider,
        (container) =>
          new InitialReelsProvider(
            container,
            new Map([
              [1, 2],
              [2, 0],
            ])
          ),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(
        T_InitialReelsComponent,
        (container) => new ResponseInitialReelsComponent(container),
        {
          lifetime: InstanceLifetime.Singleton,
        }
      );

      container.register(
        SANTA_BACKGROUND_IMAGE_RESIZE_COMPONENT_TOKEN,
        (container) => new SantaBackgroundImageResizeComponent(container),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(
        T_IGameConfigProvider,
        (container) => new CollapseGameConfigComponent(container),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(PAYTABLE_POPUP_COMPONENT_TOKEN, (c) => new SantaGameRules(c), {
        lifetime: InstanceLifetime.Singleton,
      });

      container.register(
        T_IStartSlotActionProvider,
        (container) => new SantaFreeFallSpinActionComponent(container),
        { lifetime: InstanceLifetime.Singleton }
      );

      container.register(
        SANTA_FREE_SPINS_TOTAL_WIN_COMPONENT_TOKEN,
        (c) => new SantaFreeSpinsTotalWinComponent(c),
        { lifetime: InstanceLifetime.Singleton }
      );

      if (this.isTrifecta) {
        if (EnvironmentConfig.isCheatEnabled) {
          this.container.register(
            TTF_EDITOR_POPUP_TOKEN,
            (container) => new TtfEditorPopup(container),
            { lifetime: InstanceLifetime.Singleton }
          );
        }
      }

      if (EnvironmentConfig.isCheatEnabled) {
        this.container.register(
          TEST_PERFORMANCE_MONITOR_COMPONENT_TOKEN,
          (container) => new TestPerformanceMonitorComponent(container),
          { lifetime: InstanceLifetime.Singleton }
        );
      }
    });

    this.container.registerValue(
      REQUIRED_COMPONENTS_PROVIDER_TOKEN,
      [
        APP_SETTINGS_SERVICE_TOKEN,
        ORIENTATION_HANDLER_COMPONENT_TOKEN,
        T_SubstituteIconComponent,
        T_IGameConfigProvider,
        T_EpicWinPopupProvider,
        T_StartFreeSpinsPopupComponent,
        T_EndFreeSpinsPopupComponent,
        T_ElementsStateController,
        T_InitialReelsComponent,

        SANTA_BACKGROUND_IMAGE_RESIZE_COMPONENT_TOKEN,
        SANTA_BOUNCE_ON_STOP_COMPONENT_TOKEN,
        COLLAPSE_ANIMATION_COMPONENT_TOKEN,
        COLLAPSE_WIN_COMPONENT_TOKEN,
        SANTA_FREE_SPINS_TOTAL_WIN_COMPONENT_TOKEN,

        SANTA_COLLAPSE_LOW_WIN_COMPONENT_TOKEN,
        SANTA_MULTIPLIER_HOLDER_COMPONENT_TOKEN,

        SANTA_TOP_MULTIPLIER_COMPONENT_TOKEN,
        BACKGROUND_MODE_COMPONENT_TOKEN,
        SANTA_SKIP_ANIMATIONS_COMPONENT_TOKEN,

        TTF_EDITOR_POPUP_TOKEN,
        TEST_PERFORMANCE_MONITOR_COMPONENT_TOKEN,
      ],
      { multi: true }
    );
  }
}
